import { applyWorkTimer, consumeWorkTimer, fetchWorkTimers } from '../services/workTimerService';

let timers = [];
let loaded = false;
let inflight = null;
const listeners = new Set();

const emit = () => {
  listeners.forEach((listener) => listener(timers));
};

export const subscribeWorkTimers = (listener) => {
  listeners.add(listener);
  listener(timers);
  return () => listeners.delete(listener);
};

export const getWorkTimers = () => timers;

export const findWorkTimer = (targetType, targetId) => (
  timers.find((timer) => timer?.targetType === targetType && String(timer?.targetId) === String(targetId)) || null
);

export const refreshWorkTimers = async () => {
  if (inflight) return inflight;
  inflight = fetchWorkTimers()
    .then((next) => {
      timers = next;
      loaded = true;
      emit();
      return timers;
    })
    .catch(() => {
      if (!loaded) {
        timers = [];
        emit();
      }
      return timers;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

export const ensureWorkTimers = async () => {
  if (!loaded) await refreshWorkTimers();
  return timers;
};

export const runWorkTimerAction = async (targetType, targetId, action) => {
  const updated = await applyWorkTimer(targetType, targetId, action);
  const key = `${updated?.targetType}:${updated?.targetId}`;
  const without = timers.filter((timer) => `${timer.targetType}:${timer.targetId}` !== key);
  const pausedOthers = action === 'start' || action === 'resume'
    ? without.map((timer) => (timer.running ? { ...timer, running: false, runningSince: null } : timer))
    : without;
  timers = updated ? [updated, ...pausedOthers] : pausedOthers;
  loaded = true;
  emit();
  return updated;
};

export const consumeWorkTimerMinutes = async (targetType, targetId) => {
  if (!targetType || !targetId) return null;
  const minutes = await consumeWorkTimer(targetType, targetId);
  timers = timers.filter((timer) => !(timer?.targetType === targetType && String(timer?.targetId) === String(targetId)));
  emit();
  return minutes;
};

export const liveElapsedSeconds = (timer) => {
  if (!timer) return 0;
  const accumulated = Number(timer.accumulatedSeconds) || 0;
  let elapsed = Number(timer.elapsedSeconds);
  if (!Number.isFinite(elapsed)) elapsed = accumulated;
  if (timer.running && timer.runningSince) {
    const startedMs = Date.parse(timer.runningSince);
    if (!Number.isNaN(startedMs)) {
      elapsed = accumulated + Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
    }
  }
  return Math.max(0, elapsed);
};

/** Ceil seconds to minutes, matching work-timer consume on the server. */
export const secondsToWorkMinutes = (seconds) => {
  const secs = Math.max(0, Number(seconds) || 0);
  if (secs <= 0) return null;
  return Math.max(1, Math.ceil(secs / 60));
};

export const peekWorkTimerMinutes = (targetType, targetId) => {
  if (!targetType || !targetId) return null;
  return secondsToWorkMinutes(liveElapsedSeconds(findWorkTimer(targetType, targetId)));
};

/** 2 = running now, 1 = started/paused, 0 = no timer */
export const workTimerPriority = (targetType, targetId) => {
  if (!targetType || targetId == null) return 0;
  const timer = findWorkTimer(targetType, targetId);
  if (!timer) return 0;
  if (timer.running) return 2;
  if (liveElapsedSeconds(timer) > 0) return 1;
  return 1;
};

export const compareWorkTimerPriority = (typeA, idA, typeB, idB) => (
  workTimerPriority(typeB, idB) - workTimerPriority(typeA, idA)
);
